// components/finance/sections/AccountsReceivable.jsx - MOBILE RESPONSIVE

import React, { useState } from 'react';
import { 
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowDownIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import MetricCard from '../../common/MetricCard';
import { formatCurrency } from '../../../utils';
import { useAccountsReceivable } from '../../../hooks/finance/useAccountsReceivable';

export const AccountsReceivable = () => {
  const {
    current_ar,
    overdue_ar,
    customers_overdue_count,
    customers_aging,
    loading,
    error,
    refetch,
  } = useAccountsReceivable();

  const [showAgingTable, setShowAgingTable] = useState(false);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'total_outstanding',
    direction: 'desc' 
  });

  const formatDays = (days) => {
    if (!days) return '0';
    return Math.round(days);
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getAgingColor = (amount, bucket) => {
    if (amount === 0) return 'text-gray-400';
    
    switch(bucket) {
      case 'current':
        return 'text-yellow-600 font-medium';
      case '31-60':
        return 'text-orange-600 font-medium';
      case '61-90':
        return 'text-red-600 font-medium';
      case '90+':
        return 'text-red-700 font-bold';
      default:
        return 'text-gray-900';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <MetricCard key={i} loading={true} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-red-700 text-sm">
        <ExclamationTriangleIcon className="h-5 w-5 inline mr-2" />
        Error loading A/R data: {error}
        <button 
          onClick={refetch}
          className="ml-4 text-sm underline hover:text-red-900"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!customers_aging || customers_aging.length === 0) {
    if (current_ar === 0 && overdue_ar === 0 && customers_overdue_count === 0) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500 text-center text-sm">
          No A/R data available
        </div>
      );
    }
  }

  const overduePercentage = current_ar > 0 ? (overdue_ar / current_ar) * 100 : 0;

  const sortedCustomers = customers_aging && Array.isArray(customers_aging) 
    ? [...customers_aging].sort((a, b) => {
        const aValue = a[sortConfig.key] || 0;
        const bValue = b[sortConfig.key] || 0;
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }) 
    : [];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Metric Cards - RESPONSIVE GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        
        <MetricCard
          title="Total Accounts Receivable"
          value={formatCurrency(current_ar)}
          change={null}
          changeLabel="Current Outstanding Receivables"
          icon={CurrencyDollarIcon}
          loading={false}
          isClickable={false}
        />

        <MetricCard
          title="Overdue A/R"
          value={formatCurrency(overdue_ar)}
          change={null}
          changeLabel="Current Overdue A/R"
          icon={ExclamationTriangleIcon}
          loading={false}
          onClick={() => setShowAgingTable(!showAgingTable)}
          isClickable={false}
          inverseChangeColor={false}
          subtitle=""
        />

        <MetricCard
          title="Customers Overdue"
          value={customers_overdue_count}
          change={null}
          changeLabel={`${customers_overdue_count === 1 ? 'Customer has' : 'Customers have'} overdue balances`}
          icon={UsersIcon}
          actionIcon={showAgingTable ? ArrowUpIcon : ArrowDownIcon}
          loading={false}
          onClick={() => setShowAgingTable(!showAgingTable)}
          isClickable={true}
          subtitle=""
        />
      </div>

      {/* Aging Table - RESPONSIVE */}
      {showAgingTable && sortedCustomers.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header - RESPONSIVE */}
          <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">A/R Aging by Customer</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              {sortedCustomers.length} {sortedCustomers.length === 1 ? 'customer' : 'customers'} with outstanding balances
            </p>
          </div>

          {/* Desktop Table - Hidden on mobile */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brightblue-500">
                <tr>
                  {[
                    { key: 'customer_name', label: 'Customer', align: 'left' },
                    { key: 'total_outstanding', label: 'Total Outstanding', align: 'center' },
                    { key: 'not_yet_due', label: 'Not Due', align: 'center' },
                    { key: 'current_0_30', label: 'Current (0-30)', align: 'center' },
                    { key: 'days_31_60', label: '31-60 Days', align: 'center' },
                    { key: 'days_61_90', label: '61-90 Days', align: 'center' },
                    { key: 'days_90_plus', label: '90+ Days', align: 'center' },
                    { key: 'avg_days_overdue', label: 'Avg Days', align: 'center' }
                  ].map(({ key, label, align }) => (
                    <th 
                      key={key}
                      className={`px-6 py-3 text-${align} text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-brightblue-600`}
                      onClick={() => handleSort(key)}
                    >
                      <div className={`flex items-center ${align === 'center' ? 'justify-center' : ''} space-x-1`}>
                        <span>{label}</span>
                        {sortConfig.key === key && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedCustomers.map((customer, index) => (
                  <tr key={customer.customer_ref || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="font-medium text-gray-900">
                            {customer.customer_name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="font-medium text-gray-900">
                        {formatCurrency(customer.total_outstanding || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600">
                      {formatCurrency(customer.not_yet_due || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={getAgingColor(customer.current_0_30, 'current')}>
                        {formatCurrency(customer.current_0_30 || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={getAgingColor(customer.days_31_60, '31-60')}>
                        {formatCurrency(customer.days_31_60 || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={getAgingColor(customer.days_61_90, '61-90')}>
                        {formatCurrency(customer.days_61_90 || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={getAgingColor(customer.days_90_plus, '90+')}>
                        {formatCurrency(customer.days_90_plus || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {formatDays(customer.avg_days_overdue)} days
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Shown only on mobile */}
          <div className="md:hidden divide-y divide-gray-200">
            {sortedCustomers.map((customer, index) => (
              <div 
                key={customer.customer_ref || index} 
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Customer Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {customer.customer_name || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatDays(customer.avg_days_overdue)} days avg overdue
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <div className="text-base font-bold text-gray-900">
                      {formatCurrency(customer.total_outstanding || 0)}
                    </div>
                    <div className="text-xs text-gray-500">Total</div>
                  </div>
                </div>

                {/* Aging Breakdown Grid */}
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
                  {/* Not Due */}
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-[10px] text-gray-500 uppercase font-medium">Not Due</div>
                    <div className="text-sm font-semibold text-gray-700 mt-0.5">
                      {formatCurrency(customer.not_yet_due || 0)}
                    </div>
                  </div>

                  {/* Current 0-30 */}
                  <div className="bg-yellow-50 rounded p-2">
                    <div className="text-[10px] text-yellow-700 uppercase font-medium">0-30 Days</div>
                    <div className={`text-sm font-semibold mt-0.5 ${getAgingColor(customer.current_0_30, 'current')}`}>
                      {formatCurrency(customer.current_0_30 || 0)}
                    </div>
                  </div>

                  {/* 31-60 Days */}
                  <div className="bg-orange-50 rounded p-2">
                    <div className="text-[10px] text-orange-700 uppercase font-medium">31-60 Days</div>
                    <div className={`text-sm font-semibold mt-0.5 ${getAgingColor(customer.days_31_60, '31-60')}`}>
                      {formatCurrency(customer.days_31_60 || 0)}
                    </div>
                  </div>

                  {/* 61-90 Days */}
                  <div className="bg-red-50 rounded p-2">
                    <div className="text-[10px] text-red-700 uppercase font-medium">61-90 Days</div>
                    <div className={`text-sm font-semibold mt-0.5 ${getAgingColor(customer.days_61_90, '61-90')}`}>
                      {formatCurrency(customer.days_61_90 || 0)}
                    </div>
                  </div>

                  {/* 90+ Days */}
                  {customer.days_90_plus > 0 && (
                    <div className="col-span-2 bg-red-100 rounded p-2">
                      <div className="text-[10px] text-red-800 uppercase font-bold">90+ Days</div>
                      <div className={`text-sm font-bold mt-0.5 ${getAgingColor(customer.days_90_plus, '90+')}`}>
                        {formatCurrency(customer.days_90_plus || 0)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No customers state */}
      {sortedCustomers.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 sm:p-8 text-center">
          <UsersIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm sm:text-base">No customers with outstanding balances</p>
        </div>
      )}
    </div>
  );
};

export default AccountsReceivable;